import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import { gql } from "__generated__/gql";
import { FormDialog } from "components/dialogs/Dialogs";
import DateRangePicker from "components/cck/DateRangePicker";
import { Checkbox, FormGroup, showSnackbar } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import FormField from "components/cck/FormField";
import { Contract } from "types/index";
import moment from "moment";
import { NavigateFunction } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import DatePicker from "components/cck/DatePicker";

type DuplicateContractDialogProps = {
	open: boolean,
	onClose: () => void,
	contract: Pick<Contract, "id" | "validFrom" | "validUntil">
}

type SubmitData = {
	validity: {
		validFrom?: number,
		validUntil?: number,
	}
}

const DuplicateContractDialog = ({ open, onClose, contract }: DuplicateContractDialogProps) => {

	const [ onlyValidUntil, setOnlyValidUntil ] = useState<boolean>(true);

	const navigate: NavigateFunction = useNavigate();

	const { control, handleSubmit, formState, reset } = useForm<SubmitData>({
		defaultValues: getDefaultValues(contract),
		mode: "onChange",
	});

	const handleOnClose = (): void => {
		onClose();
		reset();
	};

	const [ loading, setLoading ] = useState<boolean>(false);

	const [ duplicate ] = useMutation(DUPLICATE_CONTRACT_MUTATION);

	const duplicateContract = ({ validity }: SubmitData): void => {
		setLoading(true);

		duplicate({
			variables: {
				contractId: contract.id,
				validFrom: moment(validity.validFrom ?? null).valueOf(),
				validUntil: moment(validity.validUntil ?? null).valueOf(),
			},
		}).then((duplicate) => {
			handleOnClose();
			showSnackbar(intl.formatMessage({ id: "DUPLICATE_CONTRACT_SUCCESS" }), { variant: "success" });
			navigate(`/view/contracts/${duplicate.data?.duplicateContract.id}/details`);
		}).finally(() => {
			setLoading(false);
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "DUPLICATE_CONTRACT" })}
			form="duplicateContractForm"
			loading={loading}
		>
			<form
				onSubmit={(validity) => void handleSubmit(duplicateContract)(validity)}
				id="duplicateContractForm"
			>
				<FormGroup
					label={intl.formatMessage({ id: onlyValidUntil ? "VALID_UNTIL" : "VALID_BETWEEN" })}
					overflow="hidden"
					required
				>
					<FormField name="validity" errors={formState.errors}>
						<Controller
							name="validity"
							control={control}
							rules={{
								required: true,
								validate: value => !value.validUntil ?
									intl.formatMessage({ id: "REQUIRED" }) : true,
							}}
							render={({ field, formState }) => {

								const minValidFrom = formState.defaultValues?.validity?.validFrom ?
									new Date(formState.defaultValues.validity.validFrom) : undefined;
								const minValidUntil = formState.defaultValues?.validity?.validUntil ?
									new Date(formState.defaultValues.validity.validUntil) : undefined;

								const validFrom: Date | undefined = field.value.validFrom ? new Date(field.value.validFrom) : undefined;
								const validUntil: Date | undefined = field.value.validUntil ? new Date(field.value.validUntil) : undefined;

								if (!onlyValidUntil) {
									return <DateRangePicker
										startDate={validFrom}
										endDate={validUntil}
										minDate={minValidFrom}
										onChange={(window) =>
											field.onChange({
												// Here the fields become of type number instead of Date
												// But because they are used as argument to moment(), it does not matter
												// Still, if this ever causes problems, wrap around in Date constructor
												validFrom: window[0]?.setHours(0, 0, 0, 0),
												validUntil: window[1]?.setHours(23, 59, 59, 0),
											})
										}
									/>;
								}
								return <DatePicker
									value={field.value.validUntil}
									portalId="date-picker-container"
									popperPlacement="top-start"
									minDate={minValidUntil}
									onChange={(date) =>
										field.onChange(date ? {
											validFrom: null,
											validUntil: new Date(date).setHours(23, 59, 59, 0),
										} : { validFrom: null, validUntil: null })
									}
								/>;
							}
							}
						/>
					</FormField>
				</FormGroup>
				<Checkbox
					label={intl.formatMessage({ id: "GENERATE_START_DATE" })}
					checked={onlyValidUntil}
					onChange={() => {
						setOnlyValidUntil(!onlyValidUntil);
						reset(getDefaultValues(contract, !onlyValidUntil));
					}}
				/>
			</form>
		</FormDialog>
	);
};

const DUPLICATE_CONTRACT_MUTATION = gql(`#graphql
	mutation duplicateContract($contractId: Long!, $validFrom: Instant, $validUntil: Instant!) {
		duplicateContract(contractId: $contractId, validFrom: $validFrom, validUntil: $validUntil) {
			id
		}
	}
`);

const getDefaultValues = (contract: DuplicateContractDialogProps["contract"], onlyValidUntil: boolean = true): SubmitData => ({
	validity: {
		validFrom: contract.validFrom && !onlyValidUntil ? moment(contract.validUntil).add(1, "second").valueOf() : undefined,
		validUntil: contract.validUntil ? moment(contract.validUntil).add(1, "day").valueOf() : undefined,
	},
});

export default DuplicateContractDialog;
